import { useEffect, useState } from 'react';

import UserPreferencesContext from './UserPreferencesContext';
import { useApolloClient } from '@apollo/client';
import { UPDATE_NOTIFICATION_PREFERENCES, UPDATE_USER_PREFERENCES } from '@/graphql/mutation';
import {
  Community,
  CommunityDisclaimer,
  ContentNotifications,
  GetNotificationPreferencesResponse,
  GetUserPreferencesResponse,
  Language,
  NotificationPreferencesInput,
  PreferencesInput,
  UpdateNotificationPreferencesResponse,
  UpdateUserPreferencesResponse
} from '@/graphql/types';
import { GET_NOTIFICATION_PREFERENCES, GET_USER_PREFERENCES } from '@/graphql/queries';
import { useEffectOnMount } from '@/hooks';

const DEFAULT_LANGUAGE: Language = {
   code: 'en',
   name: 'English'
}

const DEFAULT_COMMUNITY: Community = {
  distance: 25,
  placeId: '',
  localArea: 'Niagara County',
  metricType: 'mi',
  address: '160 Passaic Ave, Lockport, NY 14094, USA',
  location: {
    lat: 43.174567,
    lng: -78.7063581,
  },
  isSaved: true,
};

const DEFAULT_DISCLAIMER: CommunityDisclaimer = {
  version: null,
  acceptedAt: null,
}

type UserPreferencesProviderType = {
  children: React.ReactNode;
};

const UserPreferencesProvider: React.FC<UserPreferencesProviderType> = ({
  children,
}) => {
  const client = useApolloClient();
  const [loading, setLoading] = useState<boolean>(false);
  const [language, setLanguage] = useState<Language>(DEFAULT_LANGUAGE);
  const [community, setCommunity] = useState<Community>(DEFAULT_COMMUNITY);
  const [viewWholeCommunityFlyers, setViewWholeCommunityFlyers] = useState<CommunityDisclaimer>(DEFAULT_DISCLAIMER);
  const [notificationPreferences, setNotificationPreferences] = useState<ContentNotifications>({
    newFlyer: false,
    savedSearch: false,
    upcomingSavedSearch: false
  })
  const [defaultLanguage] = useState<string>('en');
  const [googleApiKey] = useState<string | undefined>(
    process.env.REACT_APP_GOOGLE_API_KEY,
  );

  useEffectOnMount(( ) => {
    getUserPreferences();
    getNotificationPreferences();
  });

  useEffect(() => {
  if (language) {
    document.documentElement.lang = language.code;
  }
  }, [language])

  const getUserPreferences = async() => {
    try {
      setLoading(true);
      const { data } = await client.query<GetUserPreferencesResponse>({
        query: GET_USER_PREFERENCES
      });
      if(data?.getUserPreferences){
        const { getUserPreferences: _getUserPreferences } = data;
        setCommunity(_getUserPreferences.community);
        setLanguage(_getUserPreferences.language);
        setViewWholeCommunityFlyers(_getUserPreferences.disclaimer);
       }
    } catch(err) {
      console.log("error on getting user preferences");
    }
    finally {
      setLoading(false);
    }
  }

  const getNotificationPreferences = async() => {
    try {
      setLoading(true);
      const { data } = await client.query<GetNotificationPreferencesResponse>({
        query: GET_NOTIFICATION_PREFERENCES
      });
      if(data?.getNotificationPreferences){
        const { getNotificationPreferences: {
          content: { newFlyer, savedSearch, upcomingSavedSearch}
        } } = data;

        setNotificationPreferences({
          newFlyer,
          savedSearch,
          upcomingSavedSearch
        })
       }
    } catch(err) {
      console.log("error on getting user preferences");
    }
    finally {
      setLoading(false);
    }
  }

  const updateUserPreferences = async(input: PreferencesInput): Promise<boolean> => {
    try {
      setLoading(true);
      const { data } = await client.mutate<UpdateUserPreferencesResponse>({
        mutation: UPDATE_USER_PREFERENCES,
        variables: {
          input
        }
      });
      if(data){
        const { updateUserPreferences: { community: _community, language: _language, disclaimer: _disclaimer } } = data;
        setCommunity(_community ? _community : DEFAULT_COMMUNITY);
        setLanguage(_language ? _language : DEFAULT_LANGUAGE);
        setViewWholeCommunityFlyers(_disclaimer ? _disclaimer : DEFAULT_DISCLAIMER);
        if (input.community) {
          client.cache.modify({
            fields: {
              getAllFlyers() {
                return {
                  getAllFlyers: {
                    items: [],
                    pagination: {
                      total: 0,
                      pageNumber: 1,
                    }
                  }
                };
              }
            }
          });
        }

        return true;
       }
    } catch(err) {
      console.log("error on updating user preferences");
    }
    finally {
      setLoading(false);
    }
    return false
  }

  const updateNotificationPreferences = async(preferences: NotificationPreferencesInput): Promise<boolean> => {
    try {
      setLoading(true);
      const { data } = await client.mutate<UpdateNotificationPreferencesResponse>({
        mutation: UPDATE_NOTIFICATION_PREFERENCES,
        variables: {
          input: {
            ...preferences,
          }
        }
      });
      if(data?.updateNotificationPreferences){
        setNotificationPreferences(preferences.content);
        return true;
       }
    } catch(err) {
      console.log("error on updating user notification preferences");
    }
    finally {
      setLoading(false);
    }
    return false
  }


  const contextState = {
    language,
    community,
    viewWholeCommunityFlyers,
    notificationPreferences,
    updateUserPreferences,
    updateNotificationPreferences,
    defaultLanguage,
    googleApiKey,
    loading,
    isEnglishLanguage: language.code === DEFAULT_LANGUAGE.code,
  };

  return (
    <UserPreferencesContext.Provider value={contextState}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export default UserPreferencesProvider;
